import PageBreadcrumb from "../../components/common/CustomBreadCrumb";
import PageMeta from "../../components/common/PageMeta";
import ComponentCard from "../../components/common/ComponentCard";
import Label from "../../components/form/Label";
import Input from "../../components/form/input/InputField";
import TextArea from "../../components/form/input/TextArea";
import Radio from "../../components/form/input/Radio";
import { useEffect, useState, useRef } from "react";
import FlashcardService from "../../services/flashcard.service";
import { toast } from "react-toastify";
import { useLocation, useNavigate, useParams } from "react-router";
import { getImageDimensions, isValidURL } from "../../utils/validation";
import { uuidv4 } from "@/utils/uuid";
import { base64ToFile, resizeImage, resizeImageToFitMinimumSize } from "@/utils/reSizeImage";
import ImageCropModal from "./ImageCropModal";
import AIService from "@/services/ai.service";
import { AIIcon } from "@/icons";
import JSZip from 'jszip';
import { useTranslation } from "react-i18next";

const ChoiceAnswer = ["A", "B", "C", "D"];

export default function EditFlashcard() {
  const { flashcard_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [image, setImage] = useState<string>("");
  const [sound, setSound] = useState<string>("");
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [soundUploadProgress, setSoundUploadProgress] = useState<number>(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const soundInputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [deckData, setDeckData]: any = useState({
    title_FC: "",
    answer: "",
    image: "",
    sound: "",
    definition: "",
    moreQuestions: []
  });
  const [initialDeckData, setInitialDeckData]: any = useState(null);

  const [errors, setErrors]: any = useState({
    title_FC: "",
    answer: "",
    image: "",
    sound: "",
    definition: "",
    moreQuestions: []
  });

  const [selectedUrlOrUpload, setSelectedUrlOrUpload] = useState({
    image: "URL",
    sound: "URL"
  });
  const [showCropModal, setShowCropModal] = useState(false);
  const [tempImage, setTempImage] = useState<any>("");
  const [isLoadingGenerate, setIsLoadingGenerate] = useState(false);
  const [imageBlobs, setImageBlobs] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [forceRemoveMedia, setForceRemoveMedia] = useState({
    image: false,
    sound: false,
  });
  const [aiProvider, setAiProvider] = useState<string>(() => {
    return localStorage.getItem("aiProvider") || "gemini";
  });
  const { t } = useTranslation();
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;

  const navigateBackToList = (deckId?: string) => {
    if (returnTo) {
      navigate(returnTo);
      return;
    }

    if (deckId) {
      navigate(`/list-flashcards/${deckId}`);
      return;
    }

    navigate(-1);
  };

  const normalizeFieldValue = (value: any): any => {
    if (value instanceof File) {
      return `FILE:${value.name}:${value.size}:${value.type}:${value.lastModified}`;
    }

    if (Array.isArray(value)) {
      return value.map(normalizeFieldValue);
    }

    if (value && typeof value === "object") {
      const normalizedObject: any = {};
      for (const key of Object.keys(value)) {
        normalizedObject[key] = normalizeFieldValue(value[key]);
      }
      return normalizedObject;
    }

    return value ?? "";
  };

  const hasChanges = initialDeckData
    ? JSON.stringify(normalizeFieldValue(deckData)) !== JSON.stringify(normalizeFieldValue(initialDeckData))
    : false;

  const validate = () => {
    const newErrors: any = {
      title: "",
      description: "",
      title_FC: "",
      answer: "",
      image: "",
      sound: "",
      definition: "",
      moreQuestions: []
    };

    if (deckData.title_FC.trim() === "") {
      newErrors.title_FC = t("Question is required");
    }
    if (deckData.answer.trim() === "") {
      newErrors.answer = t("Answer is required");
    }
    if (deckData.definition.trim() === "") {
      newErrors.definition = t("Definition is required");
    }

    if (deckData.title_FC.split("\n").length > 3) {
      newErrors.title_FC = t("You can only add line breaks 3 times");
    }

    if (deckData.answer.split("\n").length > 3) {
      newErrors.answer = t("You can only add line breaks 3 times");
    }

    if (deckData.definition.split("\n").length > 3) {
      newErrors.definition = t("You can only add line breaks 3 times");
    }

    if (deckData.moreQuestions && deckData.moreQuestions.length > 0) {
      newErrors.moreQuestions = deckData.moreQuestions.map((question: any) => {
        const questionErrors: any = {};
        questionErrors.id = question.id;
        questionErrors.question = "";
        questionErrors.answer = question.type === "isYesNo" ? ["", ""] : ["", "", "", ""];
        questionErrors.type = "";
        questionErrors.correctAnswer = "";
        if (!question.question || question.question.trim() === "") {
          questionErrors.question = t("Question is required");
        }
        if (question.question.split("\n").length > 3) {
          questionErrors.question = t("You can only add line breaks 3 times");
        }
        if (!question.type) {
          questionErrors.type = t("Type is required");
        }
        let count = 0;
        for (const ans of question.answer) {
          if (!ans || ans.trim() === "") {
            questionErrors.answer[count] = t("Answer is required");
          }
          if (ans.split("\n").length > 3) {
            questionErrors.answer[count] = t("You can only add line breaks 3 times");
          }
          count++;
        }
        if (!question.correctAnswer || question.correctAnswer.trim() === "") {
          questionErrors.correctAnswer = t("Correct answer is required");
        }
        return questionErrors;
      });
    }

    const hasMainErrors = Object.keys(newErrors).some(
      key => key !== 'moreQuestions' && newErrors[key] !== ""
    );

    const hasQuestionErrors = newErrors.moreQuestions.some((question: any) => {
      if (question.question !== "" || question.type !== "" || question.correctAnswer !== "") return true;

      if (typeof question.answer === "string") {
        return question.answer !== "";
      } else {
        return question.answer.some((ans: string) => ans !== "");
      }
    });

    setErrors(newErrors);
    return hasMainErrors || hasQuestionErrors;
  };


  const handleRadioChangeImage = async (value: string) => {
    setSelectedUrlOrUpload({
      ...selectedUrlOrUpload,
      image: value
    });
    if (value === "URL") {
      setImage(deckData.image ? deckData.image : "");
    } else {
      setImage(deckData.image ? await fetchSignedUrl(deckData.image) : "");
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRadioChangeSound = async (value: string) => {
    setSelectedUrlOrUpload({
      ...selectedUrlOrUpload,
      sound: value
    });
    if (value === "URL") {
      setSound(deckData.sound ? deckData.sound : "");
    } else {
      setSound(deckData.sound ? await fetchSignedUrl(deckData.sound) : "");
    }
    if (soundInputRef.current) {
      soundInputRef.current.value = "";
    }
  };


  const fetchFlashcard = async () => {
    try {
      const response = await FlashcardService.getFlashcardById(flashcard_id || "");
      if (response.data) {
        const fetchedDeckData = {
          title_FC: response.data.title_FC,
          answer: response.data.answer,
          image: response.data.image,
          sound: response.data.sound,
          moreQuestions: response.data.moreQuestions ? response.data.moreQuestions.map((dt: any, i: any) => {
            return { id: i + 1, ...dt, correctAnswer: ChoiceAnswer[dt.answer.indexOf(dt.correctAnswer)] }
          }) : [],
          definition: response.data.definition
        };

        setDeckData(fetchedDeckData)
        setInitialDeckData(fetchedDeckData)
        setForceRemoveMedia({ image: false, sound: false });
        if (response.data.image) {
          setImage(isValidURL(response.data.image) ? response.data.image : await fetchSignedUrl(response.data.image));
        }
        if (response.data.sound) {
          setSound(isValidURL(response.data.sound) ? response.data.sound : await fetchSignedUrl(response.data.sound));
        }
        setSelectedUrlOrUpload({
          image: isValidURL(response.data.image) ? "URL" : "Upload",
          sound: isValidURL(response.data.sound) ? "URL" : "Upload",
        });
      }
    } catch (err) {
      console.error("Error fetching decks:", err);
    }
  };

  const fetchSignedUrl = async (fileKey: string): Promise<any> => {
    try {
      const res = await FlashcardService.getFile({ fileKey });
      if (res?.data?.status == 200) {
        return res?.data?.fileUrl;
      }
    } catch (err) {
      console.error("Error fetching signed URL:", err);
      return '';
    }
  };

  useEffect(() => {
    fetchFlashcard()
  }, []);

  const handleEditFlashcard = () => {
    setIsLoading(true);
    const fd = new FormData();

    const hasFieldChanged = (field: string, nextValue: any) => {
      if (!initialDeckData) {
        return true;
      }

      return JSON.stringify(normalizeFieldValue(nextValue)) !== JSON.stringify(normalizeFieldValue(initialDeckData[field]));
    };

    const resolveMediaValue = (field: "image" | "sound") => {
      const currentValue = deckData[field];
      if (forceRemoveMedia[field]) {
        return "";
      }

      if (currentValue instanceof File) {
        return currentValue;
      }

      if (typeof currentValue === "string" && currentValue.trim() !== "") {
        return currentValue;
      }

      return initialDeckData?.[field] ?? "";
    };

    let hasAnyFieldChanged = false;

    for (let key of Object.keys(deckData)) {
      if (key === "moreQuestions") {
        if (hasFieldChanged(key, deckData[key])) {
          const moreQuestions = deckData[key].map((question: any) => {
            const convertedQuestion = { ...question };
            if (ChoiceAnswer.indexOf(convertedQuestion.correctAnswer) >= 0) {
              convertedQuestion.correctAnswer = convertedQuestion.answer[ChoiceAnswer.indexOf(convertedQuestion.correctAnswer)];
            }
            return convertedQuestion;
          });
          fd.append(key, JSON.stringify(moreQuestions));
          hasAnyFieldChanged = true;
        }
      } else if (key === "image" || key === "sound") {
        const mediaValue = resolveMediaValue(key);
        const shouldSendMedia = forceRemoveMedia[key] || hasFieldChanged(key, mediaValue);
        if (shouldSendMedia) {
          fd.append(key, mediaValue);
          hasAnyFieldChanged = true;
        }
      } else if (hasFieldChanged(key, deckData[key])) {
        fd.append(key, deckData[key]);
        hasAnyFieldChanged = true;
      }
    }

    if (!hasAnyFieldChanged) {
      setIsLoading(false);
      toast.info("No changes detected");
      return;
    }

    fd.append("_id", flashcard_id || "");
    FlashcardService.editDeckFlashcardAdmin(fd)
      .then((data: any) => {
        if (data.data.status === 200) {
          toast.success("Edit Successfully!");
          setTimeout(() => {
            navigateBackToList(data.data.deckId);
          }, 1500);
        } else {
          toast.error("Edit Failed!");
          setIsLoading(false);
        }
      })
      .catch((errors) => {
        setIsLoading(false);
        toast.error("Edit Failed!");
        console.log(errors);
      });
  };

  const simulateProgress = (setProgress: (progress: number) => void, onComplete: () => void) => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += 5;
      if (progress >= 100) {
        clearInterval(interval);
        onComplete();
      } else {
        setProgress(progress);
      }
    }, 50);
  };

  const handleAddMoreQuestion = () => {
    const currentQuestions = deckData.moreQuestions || [];
    const lastId = currentQuestions.length > 0 ? currentQuestions[currentQuestions.length - 1].id : 0;
    const newQuestion = {
      id: lastId + 1,
      type: "isYesNo",
      question: "",
      answer: ["", ""],
      correctAnswer: ""
    };

    const updatedQuestions = [...currentQuestions, newQuestion];
    setDeckData({
      ...deckData,
      moreQuestions: updatedQuestions
    });
  };

  const handleRemoveQuestion = (id: any) => {
    const updatedQuestions = deckData.moreQuestions.filter((q: any) => q.id !== id);
    setDeckData({
      ...deckData,
      moreQuestions: updatedQuestions
    });
  };

  const handleRemoveAnswer = (questionData: any, answerIndex: number) => {
    const updatedQuestions = [...deckData.moreQuestions];

    const questionIndex = updatedQuestions.findIndex(q => q.id === questionData.id);

    if (questionIndex !== -1) {
      const updatedQuestion = {
        ...updatedQuestions[questionIndex],
        answer: updatedQuestions[questionIndex].answer.filter((_: any, idx: any) => idx !== answerIndex)
      };

      if (updatedQuestions[questionIndex].correctAnswer === ChoiceAnswer[answerIndex]) {
        updatedQuestion.correctAnswer = "";
      }

      updatedQuestions[questionIndex] = updatedQuestion;

      setDeckData({
        ...deckData,
        moreQuestions: updatedQuestions
      });
    }
  };

  const handleAddMoreAnswer = (questionData: any) => {
    const updatedQuestions = [...deckData.moreQuestions];

    const questionIndex = updatedQuestions.findIndex(q => q.id === questionData.id);

    if (questionIndex !== -1) {
      const updatedQuestion = {
        ...updatedQuestions[questionIndex],
        answer: [...updatedQuestions[questionIndex].answer, ""]
      };

      updatedQuestions[questionIndex] = updatedQuestion;

      setDeckData({
        ...deckData,
        moreQuestions: updatedQuestions
      });
    }
  };

  const handleQuestionChange = (id: number, type: string, value: any) => {
    let updateData: any = { [type]: value }
    if (type === "type") {
      if (value === "isYesNo") {
        updateData = { [type]: value, answer: ["", ""], correctAnswer: "" }
        const updatedQuestions = errors.moreQuestions.map((q: any) =>
          q.id === id ? { ...q, answer: ["", ""], correctAnswer: "" } : q
        );
        setErrors({
          ...errors,
          moreQuestions: updatedQuestions
        })
      }
      if (value === "isMultiple") {
        updateData = { [type]: value, answer: ["", "", "", ""], correctAnswer: "" }
        const updatedQuestions = errors.moreQuestions.map((q: any) =>
          q.id === id ? { ...q, answer: ["", "", "", ""], correctAnswer: "" } : q
        );
        setErrors({
          ...errors,
          moreQuestions: updatedQuestions
        })
      }
    }
    const updatedQuestions = deckData.moreQuestions.map((q: any) =>
      q.id === id ? { ...q, ...updateData } : q
    );
    setDeckData({
      ...deckData,
      moreQuestions: updatedQuestions
    });
  };

  const blobToBase64 = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  const handleGenerateImage = async () => {
    const newErrors: any = {
      title: "",
      description: "",
      deck_id: "",
      title_FC: "",
      answer: "",
      image: "",
      sound: "",
      category: "",
      definition: "",
      moreQuestions: [],
      imageDeck: ""
    };


    if (deckData.title_FC.trim() === "") {
      newErrors.title_FC = "Question is required";
    }
    if (deckData.answer.trim() === "") {
      newErrors.answer = "Answer is required";
    }

    const hasMainErrors = Object.keys(newErrors).some(
      key => key !== 'moreQuestions' && newErrors[key] !== ""
    );
    setErrors(newErrors);
    if (hasMainErrors) {
      return;
    }
    try {
      setIsLoadingGenerate(true);
      const res = await AIService.generateFlashcardImage({
        question: deckData.title_FC,
        answer: deckData.answer,
      }, aiProvider);
      
      const imagesList = res.data.images || [];
      if (imagesList.length !== 0) {
        setTempImage(imagesList[0]);
        setImageBlobs(imagesList);
      }
      setShowCropModal(true);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoadingGenerate(false);
    }
  }

  return (
    <div>
      <PageMeta
        title="VIYUME - VIYUME Dashboard"
        description=""
      />
      <div className="mx-auto max-w-[80%] grid grid-cols-1 gap-6">
        <div className="space-y-6">
          <ComponentCard title="">
            <div className="space-y-6">
              <div>
                <Label>{t('Question')}</Label>
                <TextArea
                  value={deckData.title_FC}
                  onChange={(value) => setDeckData({ ...deckData, title_FC: value })}
                  rows={6}
                  placeholder={`${t('Please enter question, you can only add line breaks 3 times')}`}
                />
                {errors.title_FC && (
                  <p className="mt-1 text-sm text-red-600">{errors.title_FC}</p>
                )}
              </div>
              <div>
                <Label>{t('Answer')}</Label>
                <TextArea
                  value={deckData.answer}
                  onChange={(value) => setDeckData({ ...deckData, answer: value })}
                  rows={6}
                  placeholder={`${t('Please enter answer, you can only add line breaks 3 times')}`}
                />
                {errors.answer && (
                  <p className="mt-1 text-sm text-red-600">{errors.answer}</p>
                )}
              </div>
              <div>
                <Label>{t('Definition')}</Label>
                <TextArea
                  value={deckData.definition}
                  onChange={(value) => setDeckData({ ...deckData, definition: value })}
                  rows={3}
                  placeholder={`${t('Please enter definition, you can only add line breaks 3 times')}`}
                />
                {errors.definition && (
                  <p className="mt-1 text-sm text-red-600">{errors.definition}</p>
                )}
              </div>
              <div>
                <Label>{t("Image (Optional)")}</Label>
                <div className="flex flex-wrap items-center gap-8">
                  <Radio
                    id="radio3"
                    name="choiceImage"
                    value="URL"
                    checked={selectedUrlOrUpload.image === "URL"}
                    onChange={handleRadioChangeImage}
                    label="URL"
                  />
                  <Radio
                    id="radio4"
                    name="choiceImage"
                    value="Upload"
                    checked={selectedUrlOrUpload.image === "Upload"}
                    onChange={handleRadioChangeImage}
                    label="Upload"
                  />
                </div>
                {(selectedUrlOrUpload.image === 'Upload') && (
                  <>
                    {showCropModal && (
                      <ImageCropModal
                        image={tempImage}
                        images={imageBlobs}
                        onCropComplete={(croppedImage: any) => {
                          if (typeof croppedImage === 'string') {
                            setImage(croppedImage);
                            setForceRemoveMedia((prev) => ({ ...prev, image: false }));
                            setDeckData((prev: any) => ({
                              ...prev,
                              image: base64ToFile(croppedImage, `${new Date().getTime()}.jpg`),
                            }));
                          } else if (croppedImage?.type === 'cached') {
                            setImage(croppedImage.url);
                            setForceRemoveMedia((prev) => ({ ...prev, image: false }));
                            setDeckData((prev: any) => ({
                              ...prev,
                              image: croppedImage.key,
                            }));
                            // Handle file input reset if it was set
                            if (fileInputRef.current) {
                               fileInputRef.current.value = "";
                            }
                          }
                          setShowCropModal(false);
                        }}
                        onClose={() => {
                          setTempImage("");
                          setShowCropModal(false);
                        }}
                        onCancel={() => {
                          setTempImage("");
                          setImage("");
                          setForceRemoveMedia((prev) => ({ ...prev, image: true }));
                          setDeckData((prev: any) => ({
                            ...prev,
                            image: "",
                          }));
                          if (fileInputRef.current) {
                            fileInputRef.current.value = "";
                          }
                          setShowCropModal(false);
                        }}
                      />
                    )}
                  </>
                )}
                {selectedUrlOrUpload.image === "Upload" ?
                  <>
                    <div className="flex flex-col space-y-2 mt-5 max-w-xs">
                      <Label>{t("AI Image Provider")}</Label>
                      <div className="flex flex-wrap items-center gap-4 mb-2">
                        <Radio
                          id="provider-gemini"
                          name="aiProvider"
                          value="gemini"
                          checked={aiProvider === "gemini"}
                          onChange={(val) => {
                            setAiProvider(val);
                            localStorage.setItem("aiProvider", val);
                          }}
                          label="Gemini"
                        />
                        <Radio
                          id="provider-openai"
                          name="aiProvider"
                          value="openai"
                          checked={aiProvider === "openai"}
                          onChange={(val) => {
                            setAiProvider(val);
                            localStorage.setItem("aiProvider", val);
                          }}
                          label="OpenAI"
                        />
                      </div>
                      <button
                        onClick={handleGenerateImage}
                        disabled={isLoadingGenerate}
                        className="flex items-center justify-center gap-2 bg-purple-500 hover:bg-purple-600 text-white px-6 py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                      >
                        {isLoadingGenerate ? (
                          <>
                            <svg className="w-5 h-5 animate-spin mr-2" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            {t("Generating")}...
                          </>
                        ) : (
                          <>
                            <img
                              src={AIIcon}
                              alt="AI Icon"
                              className="w-5 h-5 object-contain filter invert-[29%] sepia-[98%] saturate-[7500%] hue-rotate-[245deg] brightness-[90%] contrast-[90%]"
                            />
                            {t("Generate Image with AI")}
                          </>
                        )}
                      </button>

                      <div className="relative">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
                              if (!validImageTypes.includes(file.type)) {
                                setErrors((prev: any) => ({
                                  ...prev,
                                  image: 'Please select a valid image file (JPEG, PNG, GIF, WEBP)'
                                }));
                                setImage("");
                                setDeckData({
                                  ...deckData,
                                  image: "",
                                })
                                if (fileInputRef.current) {
                                  fileInputRef.current.value = "";
                                }
                                return;
                              }

                              try {
                                const dimensions: any = await getImageDimensions(file);
                                if (dimensions) {
                                  if (dimensions.width < 800 || dimensions.width > 1200 || dimensions.height < 800 || dimensions.height > 1200) {
                                    const resizedFile: any = await resizeImageToFitMinimumSize(file, 400, 400);
                                    setErrors((prev: any) => ({
                                      ...prev,
                                      image: ""
                                    }));
                                    setUploadProgress(0);
                                    const reader = new FileReader();

                                    simulateProgress(setUploadProgress, () => {
                                      reader.readAsDataURL(resizedFile);
                                    });
                                    reader.onload = (event) => {
                                      if (event.target?.result) {
                                        const base64String = event.target.result.toString();
                                        setUploadProgress(100);
                                        setTempImage(base64String);
                                        setTimeout(() => {
                                          setUploadProgress(0);
                                          setShowCropModal(true);
                                        }, 1000);
                                      }
                                    };
                                    reader.onerror = () => {
                                      toast.error('Error reading file');
                                      setUploadProgress(0);
                                    };
                                    return
                                  }
                                }
                              } catch (error) {
                                setErrors((prev: any) => ({
                                  ...prev,
                                  image: 'Failed to read image dimensions'
                                }));
                                setImage("");
                                setDeckData({
                                  ...deckData,
                                  image: "",
                                })
                                if (fileInputRef.current) {
                                  fileInputRef.current.value = "";
                                }
                                return;
                              }

                              setErrors((prev: any) => ({
                                ...prev,
                                image: ""
                              }));
                              setUploadProgress(0);
                              const reader = new FileReader();

                              simulateProgress(setUploadProgress, () => {
                                reader.readAsDataURL(file);
                              });
                              reader.onload = (event) => {
                                if (event.target?.result) {
                                  const base64String = event.target.result.toString();
                                  setUploadProgress(100);
                                  setTempImage(base64String);
                                  setTimeout(() => {
                                    setUploadProgress(0);
                                    setShowCropModal(true);
                                  }, 1000);
                                }
                              };
                              reader.onerror = () => {
                                toast.error('Error reading file');
                                setUploadProgress(0);
                              };
                            }
                          }}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                          id="file-upload"
                        />
                        <label
                          htmlFor="file-upload"
                          className="flex items-center justify-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white px-6 py-3 rounded-lg transition-colors cursor-pointer font-medium"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                          </svg>
                          {t("Upload Image")}
                        </label>
                      </div>
                    </div>

                    {uploadProgress > 0 && uploadProgress < 100 && (
                      <div className="mt-2">
                        <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                          <div
                            className="bg-indigo-600 h-2.5 rounded-full transition-all duration-300 ease-in-out"
                            style={{ width: `${uploadProgress}%` }}
                          ></div>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">{uploadProgress}% uploaded</p>
                      </div>
                    )}

                    {image && (
                      <div className="mt-4 relative inline-flex items-center gap-2">
                        <img
                          src={image}
                          alt="Preview"
                          className="max-w-xs rounded-lg shadow-lg"
                          onClick={() => setIsModalOpen(true)}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setImage("");
                            setForceRemoveMedia((prev) => ({ ...prev, image: true }));
                            setDeckData({
                              ...deckData,
                              image: "",
                            })
                            if (fileInputRef.current) {
                              fileInputRef.current.value = "";
                            }
                          }}
                          className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600 focus:outline-none"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    )}
                    {errors.image && (
                      <p className="mt-1 text-sm text-red-500">{errors.image}</p>
                    )}
                  </> :
                  <>
                    <Input
                      type="text"
                      value={deckData.image}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setForceRemoveMedia((prev) => ({ ...prev, image: e.target.value.trim() === "" }));
                        setDeckData({ ...deckData, image: e.target.value })
                        setImage(e.target.value)
                      }}
                      placeholder="Please enter image URL"
                      className="mt-2"
                    />
                    {image && (
                      <div className="mt-4 relative inline-flex items-center gap-2">
                        <img
                          src={image}
                          alt="Preview"
                          className="max-w-xs rounded-lg shadow-lg"
                          onClick={() => setIsModalOpen(true)}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setImage("");
                            setDeckData({
                              ...deckData,
                              image: "",
                            })
                            if (fileInputRef.current) {
                              fileInputRef.current.value = "";
                            }
                          }}
                          className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600 focus:outline-none"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </>
                }

              </div>
              <div>
                <Label>{t('Sound (Optional)')}</Label>
                <div className="flex flex-wrap items-center gap-8">
                  <Radio
                    id="radio5"
                    name="choiceSound"
                    value="URL"
                    checked={selectedUrlOrUpload.sound === "URL"}
                    onChange={handleRadioChangeSound}
                    label="URL"
                  />
                  <Radio
                    id="radio6"
                    name="choiceSound"
                    value="Upload"
                    checked={selectedUrlOrUpload.sound === "Upload"}
                    onChange={handleRadioChangeSound}
                    label="Upload"
                  />
                </div>
                {selectedUrlOrUpload.sound === "Upload" ?
                  <>
                    <div className="flex flex-col space-y-2 mt-5 max-w-xs">
                      <div className="relative">
                        <input
                          ref={soundInputRef}
                          type="file"
                          accept="audio/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const validAudioTypes = ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/mp3'];
                              if (!validAudioTypes.includes(file.type)) {
                                setErrors((prev: any) => ({
                                  ...prev,
                                  sound: 'Please select a valid audio file (MP3, WAV, OGG)'
                                }));
                                setSound("");
                                setDeckData({
                                  ...deckData,
                                  sound: "",
                                })
                                if (soundInputRef.current) {
                                  soundInputRef.current.value = "";
                                }
                                return;
                              }

                              if (file.size > 25 * 1024 * 1024) {
                                setErrors((prev: any) => ({
                                  ...prev,
                                  sound: 'File size must be less than 25MB'
                                }));
                                setSound("");
                                setDeckData({
                                  ...deckData,
                                  sound: "",
                                })
                                if (soundInputRef.current) {
                                  soundInputRef.current.value = "";
                                }
                                return;
                              }

                              // Clear sound error when valid file is selected
                              setErrors((prev: any) => ({
                                ...prev,
                                sound: ""
                              }));

                              setSoundUploadProgress(0);
                              const reader = new FileReader();

                              simulateProgress(setSoundUploadProgress, () => {
                                reader.readAsDataURL(file);
                              });

                              reader.onload = (event) => {
                                if (event.target?.result) {
                                  const base64String = event.target.result.toString();
                                  setSoundUploadProgress(100);
                                  setTimeout(() => {
                                    setSoundUploadProgress(0);
                                    setSound(base64String);
                                    setForceRemoveMedia((prev) => ({ ...prev, sound: false }));
                                    setDeckData({
                                      ...deckData,
                                      sound: file,
                                    })
                                  }, 1000);
                                }
                              };

                              reader.onerror = () => {
                                toast.error('Error reading file');
                                setSoundUploadProgress(0);
                              };

                              reader.readAsDataURL(file);
                            }
                          }}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                          id="sound-upload"
                        />
                        <label
                          htmlFor="sound-upload"
                          className="flex items-center justify-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white px-6 py-3 rounded-lg transition-colors cursor-pointer font-medium"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                          </svg>
                          {t('Upload Sound')}
                        </label>
                      </div>
                    </div>


                    {soundUploadProgress > 0 && soundUploadProgress < 100 && (
                      <div className="mt-2">
                        <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                          <div
                            className="bg-indigo-600 h-2.5 rounded-full transition-all duration-300 ease-in-out"
                            style={{ width: `${soundUploadProgress}%` }}
                          ></div>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">{soundUploadProgress}% uploaded</p>
                      </div>
                    )}

                    {sound && (
                      <div className="mt-4 flex items-center gap-2">
                        <audio controls>
                          <source src={sound} type="audio/mpeg" />
                          Your browser does not support the audio element.
                        </audio>
                        <button
                          type="button"
                          onClick={() => {
                            setSound("");
                            setForceRemoveMedia((prev) => ({ ...prev, sound: true }));
                            setDeckData({
                              ...deckData,
                              sound: "",
                            })
                            if (soundInputRef.current) {
                              soundInputRef.current.value = "";
                            }
                          }}
                          className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600 focus:outline-none"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    )}
                    {errors.sound && (
                      <p className="mt-1 text-sm text-red-500">{errors.sound}</p>
                    )}
                  </> :
                  <>
                    <Input
                      type="text"
                      value={deckData.sound}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setForceRemoveMedia((prev) => ({ ...prev, sound: e.target.value.trim() === "" }));
                        setDeckData({ ...deckData, sound: e.target.value })
                        setSound(e.target.value)
                      }}
                      placeholder="Please enter sound URL"
                      className="mt-2"
                    />
                    {sound && (
                      <div className="mt-4 flex items-center gap-2">
                        <audio controls>
                          <source src={sound} type="audio/mpeg" />
                          Your browser does not support the audio element.
                        </audio>
                        <button
                          type="button"
                          onClick={() => {
                            setSound("");
                            setForceRemoveMedia((prev) => ({ ...prev, sound: true }));
                            setDeckData({
                              ...deckData,
                              sound: "",
                            })
                            if (soundInputRef.current) {
                              soundInputRef.current.value = "";
                            }
                          }}
                          className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600 focus:outline-none"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </>
                }
              </div>
            </div>
            {deckData.moreQuestions.length !== 0 && <hr className="my-6 border-gray-300 dark:border-gray-700" />}
            {deckData.moreQuestions.map((dt: any, i: number) => (
              <div key={dt.id}>
                <Label className="font-bold text-xl mt-4 mb-2">{t('Question')} {i + 1}</Label>
                <Label>{t('Type')}</Label>
                <div className="flex flex-wrap items-center gap-8">
                  <Radio
                    id={uuidv4()}
                    name={`choiceTypeQuestion-${dt.id}`}
                    value="isYesNo"
                    checked={dt.type === "isYesNo"}
                    onChange={() => handleQuestionChange(dt.id, "type", "isYesNo")}
                    label="True / False"
                  />
                  <Radio
                    id={uuidv4()}
                    name={`choiceTypeQuestion-${dt.id}`}
                    value="isMultiple"
                    checked={dt.type === "isMultiple"}
                    onChange={() => handleQuestionChange(dt.id, "type", "isMultiple")}
                    label="Multiple"
                  />
                  <button
                    type="button"
                    className="inline-flex items-center px-4 py-2 border border-red-400 shadow-sm text-sm font-medium rounded-md text-red-800 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    onClick={() => handleRemoveQuestion(dt.id)}
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M10 3h4a1 1 0 011 1v1H9V4a1 1 0 011-1z" />
                    </svg>
                    {t('Remove')}
                  </button>
                </div>

                <div className="mt-3">
                  <Label>{t('Question')}</Label>
                  <TextArea
                    value={dt.question}
                    onChange={(value) => handleQuestionChange(dt.id, "question", value)}
                    rows={6}
                    placeholder="Please enter question, you can only add line breaks 3 times"
                  />
                  {errors.moreQuestions.find((g: any) => dt.id === g.id) && (
                    <p className="mt-1 text-sm text-red-600">{errors.moreQuestions.find((g: any) => dt.id === g.id)?.question}</p>
                  )}
                </div>

                {dt.answer.map((ans: string, ind: number) => {
                  return (
                    <div className="mt-3" key={`${dt.id}-${ind}`}>
                      {dt.type === "isMultiple" ?
                        <Label>{t('Answer')} {ChoiceAnswer[ind]}</Label> :
                        <div className="flex items-center justify-between">
                          <Label>{t('Answer')} {ind + 1}</Label>
                          {dt.answer.length > 2 && (
                            <button
                              type="button"
                              className="inline-flex items-center px-3 py-1 border border-red-400 shadow-sm text-xs font-medium rounded-md text-red-800 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                              onClick={() => handleRemoveAnswer(dt, ind)}
                            >
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M10 3h4a1 1 0 011 1v1H9V4a1 1 0 011-1z" />
                              </svg>
                              {t('Remove')}
                            </button>
                          )}
                        </div>
                      }
                      <Radio
                        id={uuidv4()}
                        name={`choiceCorrectAnswer-${dt.id}-${ind}`}
                        value={ChoiceAnswer[ind]}
                        checked={dt.correctAnswer === ChoiceAnswer[ind]}
                        onChange={() => handleQuestionChange(dt.id, "correctAnswer", ChoiceAnswer[ind])}
                        label={t('Correct Answer')}
                        className="mb-2"
                      />
                      <TextArea
                        value={ans}
                        onChange={(value) => {
                          const updatedAnswers = [...dt.answer];
                          updatedAnswers[ind] = value;
                          handleQuestionChange(dt.id, "answer", updatedAnswers);
                        }}
                        rows={6}
                        placeholder={`${t('Please enter answer, you can only add line breaks 3 times')}`}
                        disabled={dt.type !== "isMultiple"}
                      />
                      {errors.moreQuestions.find((g: any) => dt.id === g.id) && (
                        <p className="mt-1 text-sm text-red-600">{errors.moreQuestions.find((g: any) => dt.id === g.id)?.answer[ind]}</p>
                      )}
                    </div>
                  )
                })}
                {(errors.moreQuestions.find((g: any) => dt.id === g.id) && errors.moreQuestions.find((g: any) => dt.id === g.id))?.answer.every((val: any) => val === "") && (
                  <p className="mt-1 text-sm text-red-600">{errors.moreQuestions.find((g: any) => dt.id === g.id)?.correctAnswer}</p>
                )}
                {dt.type === "isYesNo" &&
                  <div className="flex justify-end">
                    <button
                      type="button"
                      className="mt-3 inline-flex items-center px-4 py-2 border border-blue-400 shadow-sm text-sm font-medium rounded-md text-blue-800 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      onClick={() => handleAddMoreAnswer(dt)}
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                      </svg>
                      {t('Add Answer')}
                    </button>
                  </div>
                }
                {(deckData.moreQuestions.length !== 1 && deckData.moreQuestions.length - 1 !== i) && (
                  <hr className="my-6 border-gray-300 dark:border-gray-700" />
                )}
              </div>
            ))}
            <hr className="my-6 border-gray-300 dark:border-gray-700" />
            <div className="flex justify-between items-center">
              <button
                type="button"
                className="inline-flex items-center px-4 py-2 border border-yellow-400 shadow-sm text-sm font-medium rounded-md text-yellow-800 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                onClick={handleAddMoreQuestion}
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
                {t('Add Question')}
              </button>

              <div className="flex">
                <button
                  type="button"
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  onClick={() => navigateBackToList()}
                >
                  {t('Cancel')}
                </button>
                <div className="mx-2"></div>
                <button
                  type="button"
                  disabled={isLoading || !hasChanges}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  onClick={() => {
                    if (!isLoading && hasChanges) {
                      const hasErrors = validate();
                      if (!hasErrors) {
                        handleEditFlashcard();
                      } else {
                        toast.warning("Validation failed - Please check your inputs");
                      }
                    }
                  }}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center gap-2">
                      <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      {t('Editing')} ...
                    </div>
                  ) : (
                    <> {t('Edit')}</>
                  )}
                </button>
              </div>
            </div>
            {isModalOpen && (
              <>
                <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-70">
                  <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl p-4 relative m-4">
                    <button
                      onClick={() => setIsModalOpen(false)}
                      className="absolute -top-2 -right-2 z-50 bg-gray-800 text-white hover:bg-gray-900 w-8 h-8 rounded-full flex items-center justify-center text-lg transition-colors"
                    >
                      &times;
                    </button>
                    <div className="h-[80vh] flex items-center justify-center">
                      <img
                        src={image}
                        alt="Preview"
                        className="max-h-full max-w-full object-contain rounded-md"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </ComponentCard>
        </div>
      </div>
    </div>
  );
}
